/**
 * Submissions API Module
 * API calls for form submission management
 */

import apiClient from '../core';
import type {
  FormSubmission,
  SubmissionStats,
  SubmissionListResponse,
  UnreadCountResponse,
  SubmissionListParams,
} from '../../types/submissions';

// Re-export types for convenience
export type {
  FormSubmission,
  SubmissionStats,
  SubmissionListResponse,
  UnreadCountResponse,
  SubmissionListParams,
};

// ============================================================================
// API Functions
// ============================================================================

export const submissionsApi = {
  /**
   * List submissions for a site with pagination
   */
  async listSubmissions(
    siteId: string,
    params?: SubmissionListParams
  ): Promise<SubmissionListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.includeArchived) searchParams.set('includeArchived', 'true');
    if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.search) searchParams.set('search', params.search);

    const queryString = searchParams.toString();
    const url = `/submissions/site/${siteId}${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<SubmissionListResponse>(url);
    return response.data;
  },

  /**
   * List submissions for a specific form component
   */
  async listSubmissionsByComponent(
    siteId: string,
    componentId: string,
    params?: SubmissionListParams
  ): Promise<SubmissionListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.includeArchived) searchParams.set('includeArchived', 'true');
    if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');

    const queryString = searchParams.toString();
    const url = `/submissions/site/${siteId}/component/${componentId}${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<SubmissionListResponse>(url);
    return response.data;
  },

  /**
   * Get submission statistics for a site
   */
  async getStats(siteId: string): Promise<SubmissionStats> {
    const response = await apiClient.get<SubmissionStats>(`/submissions/site/${siteId}/stats`);
    return response.data;
  },

  /**
   * Get unread submission count for a site
   */
  async getUnreadCount(siteId: string): Promise<number> {
    const response = await apiClient.get<UnreadCountResponse>(
      `/submissions/site/${siteId}/unread-count`
    );
    return response.data.unreadCount;
  },

  /**
   * Get a single submission by ID
   */
  async getSubmission(siteId: string, submissionId: string): Promise<FormSubmission> {
    const response = await apiClient.get<FormSubmission>(
      `/submissions/site/${siteId}/${submissionId}`
    );
    return response.data;
  },

  /**
   * Mark a submission as read
   */
  async markAsRead(siteId: string, submissionId: string): Promise<FormSubmission> {
    const response = await apiClient.patch<FormSubmission>(
      `/submissions/site/${siteId}/${submissionId}/read`
    );
    return response.data;
  },

  /**
   * Toggle archive status of a submission
   */
  async toggleArchive(
    siteId: string,
    submissionId: string,
    archived: boolean
  ): Promise<FormSubmission> {
    const response = await apiClient.patch<FormSubmission>(
      `/submissions/site/${siteId}/${submissionId}/archive`,
      { archived }
    );
    return response.data;
  },

  /**
   * Delete a submission
   */
  async deleteSubmission(siteId: string, submissionId: string): Promise<void> {
    await apiClient.delete(`/submissions/site/${siteId}/${submissionId}`);
  },
};

export default submissionsApi;
